import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BrainCircuit, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import {
  useContractorSuggestion,
  type SuggestScope,
  type SuggestedContractor,
} from "../api/knowledgeNotes";

/**
 * Podpowiedź podwykonawcy pod pickerem firmy w formularzu zlecenia (WPW, zwykłe
 * zapotrzebowanie, panele etapów). Ten sam silnik, co krąg odbiorców ogłoszenia etapu:
 * fakty z historii + oś czasu zleceń na przedsięwzięciu + „Zasady podpowiadania" + AI.
 * Niczego nie wymusza — klik „Wybierz" tylko ustawia picker, ręczny wybór zostaje.
 */

interface Props {
  scope: SuggestScope;
  projectId?: string;
  connectionId?: string;
  /** Krąg = firmy z pickera; bez tego krąg wynika z rodzaju zlecenia (backend). */
  candidates?: string[];
  selectedId?: string;
  onPick: (contractorId: string) => void;
  /** false = nie pytaj (np. picker jeszcze się ładuje albo nie ma projektu). */
  enabled?: boolean;
  /** Termin realizacji z formularza (yyyy-mm-dd) — włącza dopisek „planowany urlop od–do"
   *  przy firmach, które mają urlop w trakcie zlecenia. */
  deadline?: string;
}

function facts(row: SuggestedContractor): string {
  return [
    row.orders ? `${row.orders} zleceń u nas` : "",
    row.project_orders_total ? `${row.project_orders_total} na tym przedsięwzięciu` : "",
    row.offers ? `${row.offers} ofert` : "",
    row.rating !== null ? `ocena ${row.rating.toFixed(1)}` : "",
    row.distance_km !== null ? `${row.distance_km.toFixed(0)} km` : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Dopiski za nazwą firmy: „(urlop 12.10.2026–20.10.2026)" — urlop w oknie składania
 *  zamówienia (firma na dole listy, nadal do wyboru) — oraz „(planowany urlop od–do)" —
 *  inny urlop w terminie realizacji zlecenia. Backend pilnuje, że jeden urlop nie trafia
 *  do obu; oba dopiski naraz = dwa osobne urlopy. */
function LeaveSuffix({ row }: { row: Pick<SuggestedContractor, "leave_label" | "planned_leave_label"> }) {
  return (
    <>
      {row.leave_label && (
        <span className="ml-1 font-normal text-amber-700 dark:text-amber-400" data-testid="contractor-leave-suffix">
          ({row.leave_label})
        </span>
      )}
      {row.planned_leave_label && (
        <span
          className="ml-1 font-normal text-sky-700 dark:text-sky-400"
          data-testid="contractor-planned-leave-suffix"
          title="Firma ma urlop w terminie realizacji tego zlecenia"
        >
          ({row.planned_leave_label})
        </span>
      )}
    </>
  );
}

const ELIGIBLE_VIA_LABEL: Record<string, string> = {
  work_type: "typ prac w kartotece",
  line_item: "pozycja WPW w cenniku",
};

/** Skąd firma jest w kręgu (tylko WPW): typ prac w kartotece („Negocjator WPW” /
 *  „Geodeta z WPW”) i/lub pozycja WPW w cenniku umowy ramowej — żeby było widać, że firma
 *  geodezyjna trafiła tu z cennika, nie z typu. */
function EligibleVia({ row }: { row: Pick<SuggestedContractor, "eligible_via"> }) {
  if (!row.eligible_via?.length) return null;
  return (
    <span
      className="ml-1 text-[10px] font-normal text-[var(--color-text-muted)]"
      data-testid="contractor-eligible-via"
      title="Skąd firma jest w kręgu tego rodzaju zlecenia"
    >
      · {row.eligible_via.map((v) => ELIGIBLE_VIA_LABEL[v] ?? v).join(" + ")}
    </span>
  );
}

function recentLine(row: SuggestedContractor): string {
  const last = row.project_orders_timeline[0];
  if (!last) return "";
  const age = last.hours_ago < 48 ? `${Math.round(last.hours_ago)} h temu` : `${Math.round(last.hours_ago / 24)} dni temu`;
  return `ostatnio na tym przedsięwzięciu: ${last.order_type_label} (${age})`;
}

export function ContractorSuggestionPanel({
  scope,
  projectId,
  connectionId,
  candidates,
  selectedId,
  onPick,
  enabled = true,
  deadline,
}: Props) {
  const payload = useMemo(
    () =>
      enabled
        ? {
            scope,
            ...(connectionId ? { connection: connectionId } : projectId ? { project: projectId } : {}),
            ...(candidates && candidates.length ? { candidates } : {}),
            ...(deadline ? { deadline } : {}),
          }
        : null,
    [enabled, scope, projectId, connectionId, candidates, deadline],
  );
  const q = useContractorSuggestion(payload, enabled);
  const [showAll, setShowAll] = useState(false);
  const [showRules, setShowRules] = useState(false);

  if (!enabled) return null;

  const rows = q.data?.contractors ?? [];
  const picked = rows.filter((r) => r.preselected);
  const rules = q.data?.rules ?? [];
  const selected = selectedId ? rows.find((r) => r.id === selectedId) : undefined;

  return (
    <div
      data-testid="contractor-suggestion-panel"
      className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-xs"
    >
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-[var(--color-text-secondary)]">
        <Sparkles size={12} className={q.isLoading ? "animate-pulse" : ""} />
        Podpowiedź podwykonawcy
        {q.data && (
          <span className="font-normal text-[var(--color-text-muted)]">
            {q.data.source === "ai"
              ? "· AI na podstawie historii zleceń, ofert, ocen i zasad podpowiadania"
              : q.data.source === "rules"
                ? "· punktacja z historii (AI niedostępne)"
                : ""}
          </span>
        )}
      </div>

      {q.isLoading && (
        <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
          Dobieram firmy z historii współpracy…
        </p>
      )}
      {q.isError && (
        <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
          Podpowiedź niedostępna — wybierz firmę ręcznie.
        </p>
      )}
      {q.data && rows.length === 0 && (
        <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
          Brak firm w kręgu tego rodzaju zlecenia.
        </p>
      )}

      {q.data && q.data.note && (
        <p className="mt-1 text-[11px] leading-snug text-[var(--color-text-muted)]">{q.data.note}</p>
      )}

      {picked.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {picked.map((row) => (
            <li key={row.id} className="flex items-start gap-2">
              <button
                type="button"
                onClick={() => onPick(row.id)}
                className={`shrink-0 rounded border px-2 py-0.5 text-[11px] font-medium ${
                  row.id === selectedId
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                    : "border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text)] hover:border-[var(--color-primary)]"
                }`}
              >
                {row.id === selectedId ? "Wybrana" : "Wybierz"}
              </button>
              <span className="min-w-0">
                <span className="block font-medium text-[var(--color-text)]">
                  {row.name}
                  <LeaveSuffix row={row} />
                  <EligibleVia row={row} />
                  {row.preferred_by_rule && (
                    <span className="ml-1 rounded bg-emerald-100 px-1 text-[10px] text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                      zasada
                    </span>
                  )}
                </span>
                <span className="block text-[11px] leading-snug text-[var(--color-text-secondary)]">
                  {row.reason || facts(row) || "brak historii u nas"}
                </span>
                {recentLine(row) && (
                  <span className="block text-[10px] text-[var(--color-text-muted)]">{recentLine(row)}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {q.data && rows.length > 0 && picked.length === 0 && (
        <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
          Żadna firma nie została zaproponowana — wybierz ręcznie z listy.
        </p>
      )}

      {/* Firma wybrana ręcznie, której podpowiedź nie zaproponowała — pokazujemy, dlaczego
          (np. zasada „nie podpowiadaj"), ale niczego nie blokujemy. */}
      {selected && !selected.preselected && (
        <p
          data-testid="contractor-suggestion-selected-note"
          className={`mt-1.5 text-[11px] leading-snug ${
            selected.excluded_by_rule ? "text-amber-700 dark:text-amber-400" : "text-[var(--color-text-muted)]"
          }`}
        >
          {selected.name}
          <LeaveSuffix row={selected} />: {selected.reason || "nie na liście podpowiedzi"}
          {facts(selected) ? ` (${facts(selected)})` : ""}
        </p>
      )}

      {q.data && rows.length > picked.length && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-1.5 inline-flex items-center gap-0.5 text-[11px] text-[var(--color-text-muted)] underline hover:text-[var(--color-primary)]"
        >
          {showAll ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          {showAll ? "Ukryj pozostałe" : `Pozostałe firmy w kręgu (${rows.length - picked.length})`}
        </button>
      )}
      {showAll && (
        <ul className="mt-1 space-y-0.5">
          {rows
            .filter((r) => !r.preselected)
            .map((row) => (
              <li key={row.id} className="flex items-start gap-2 text-[11px]">
                <button
                  type="button"
                  onClick={() => onPick(row.id)}
                  className="shrink-0 text-[var(--color-text-muted)] underline hover:text-[var(--color-primary)]"
                >
                  wybierz
                </button>
                <span className="min-w-0">
                  <span className={row.excluded_by_rule ? "text-amber-700 dark:text-amber-400" : "text-[var(--color-text)]"}>
                    {row.name}
                  </span>
                  <LeaveSuffix row={row} />
                  <EligibleVia row={row} />
                  <span className="text-[var(--color-text-muted)]">
                    {" "}
                    — {row.reason || facts(row) || "brak historii u nas"}
                  </span>
                </span>
              </li>
            ))}
        </ul>
      )}

      {rules.length > 0 && (
        <div className="mt-1.5">
          <button
            type="button"
            onClick={() => setShowRules((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] text-[var(--color-text-muted)] underline hover:text-[var(--color-primary)]"
          >
            <BrainCircuit size={11} />
            {showRules ? "Ukryj zasady" : `Zasady podpowiadania w grze (${rules.length})`}
          </button>
          {showRules && (
            <ul className="mt-1 space-y-0.5 text-[11px] text-[var(--color-text-secondary)]">
              {rules.map((rule) => (
                <li key={rule.id}>
                  <span className="font-mono text-[10px] text-[var(--color-text-muted)]">[Z-{rule.index}]</span>{" "}
                  {rule.contractor_name ? `${rule.contractor_name}: ` : ""}
                  {rule.body}
                  <span className="text-[var(--color-text-muted)]">
                    {" "}
                    ({rule.effect_label}
                    {rule.scope ? `, ${rule.scope_label}` : ""})
                  </span>
                </li>
              ))}
              <li>
                <Link
                  to="/contractors/suggestion-rules"
                  className="text-[var(--color-text-muted)] underline hover:text-[var(--color-primary)]"
                >
                  Rejestr zasad podpowiadania
                </Link>
              </li>
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
